/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {addShellPlaceholder} from './page';
import {addVersionToMetricsConfig, getSPAVersion, sendAndReceive} from './util';
import type {AdobeMetricsWithRecents} from './runtime';
import {AIApi} from './ai';
import {AppApi} from './api/AppApi';
import {CacheApi} from './cache';
import {CapabilityApi} from './api/CapabilityApi';
import {ComponentApi} from './component';
import type {ConfigProxyManager} from './config';
import type {Configuration} from '@exc/metrics-runtime';
import type {ConfigureListener, RuntimeMessenger, RuntimeModulesSync, RuntimeObject} from './models/runtimeModels';
import {ConsentApi} from './consent';
import createActions from './actions';
import {createGettify, getPerformanceEntries, GettifyFn} from './utilDeferred';
import createRecents from './recents';
import {
  CustomKeyboardEvent,
  CustomMouseEvent,
  digitalDataUpdated,
  DonePayload,
  Events,
  extractTokenMetadata,
  sanitizeConfiguration,
  UserKeyEvent,
  UserMouseEvent
} from '@exc/shared';
import {enable as enableToastTracking} from './toast';
import {FeatureFlagsApi} from './featureflags';
import type {Gainsight} from '@adobe/exc-app/RuntimeConfiguration';
import {getQueryParams} from '@exc/url/query';
import type {InternalApi} from '@adobe/exc-app/internal';
import type {Metrics, MetricsApi} from '@adobe/exc-app/metrics';
import {ModulesApi} from './api/ModulesApi';
import {NetworkPrefetchApi} from './network/prefetch';
import type {PerformanceRecord} from '@adobe/exc-app/page';
import {PermissionsApi} from './permissions';
import {PulseApi} from './pulse';
import type {Runtime, RuntimeConfiguration} from '@adobe/exc-app';
import {SessionApi} from './session';
import {SettingsApi} from './settings';
import {ShellApi} from './shell';
import {SidenavApi} from './sidenav';
import {UserApi} from './user';
import {watchHash, watchHistory, watchLocation} from './history';

export interface RuntimeEngineSync {
  configurePromise: Promise<void>;
  modules: RuntimeModulesSync;
  /**
   /**
   * Called when the channel communication is established and the
   * config is fetched; generally application will bootstrap and start
   * rendering after this callback is invoked.
   * @param {RuntimeObject} runtime - Runtime instance
   * @param {RuntimeConfiguration} config - Configuration payload coming from the Shell frame
   */
  onConfigure: (runtime: RuntimeObject, config: RuntimeConfiguration, internal: InternalApi) => Promise<void>;
  /**
   * Checks for the window.digitalData object required for Target
   * Recommendations service and stores the value to check for changes.
   * @param {RuntimeObject} runtime - Runtime instance
   */
  onPageChange: (runtime: RuntimeObject) => void;
  /**
   * This function prepares the events to be sent to the iframe.
   * @param {UserKeyEvent|UserMouseEvent} e The event from the outer shell.
   */
  onShellUserInput: (e: UserKeyEvent | UserMouseEvent) => void;
  /**
   * Waits for Runtime to be instantiated.
   * This method will log sessions where Runtime is not instantiated in a timely manner.
   * @param {instance: Runtime} runtimeInstance
   */
  waitForInit: (runtimeInstance: {instance: Runtime|undefined}) => void;
}

let configureResolve: () => void;
const configurePromise = new Promise<void>(resolve => configureResolve = resolve);

const createModules = (
  messenger: RuntimeMessenger,
  configManager: ConfigProxyManager,
  gettify: GettifyFn,
  metricsApi: MetricsApi
): RuntimeModulesSync => ({
  ai: new AIApi(messenger, configManager, gettify, metricsApi),
  app: new AppApi(messenger, configManager, gettify, metricsApi),
  cache: new CacheApi(messenger, configManager, gettify, metricsApi),
  capability: new CapabilityApi(messenger, configManager, gettify, metricsApi),
  component: new ComponentApi(messenger, configManager, gettify, metricsApi),
  consent: new ConsentApi(messenger, configManager, gettify, metricsApi),
  featureFlags: new FeatureFlagsApi(messenger, configManager, gettify, metricsApi),
  modules: new ModulesApi(messenger, configManager, gettify, metricsApi),
  networkPrefetch: new NetworkPrefetchApi(messenger, configManager, gettify, metricsApi),
  permissions: new PermissionsApi(messenger, configManager, gettify, metricsApi),
  pulse: new PulseApi(messenger, configManager, gettify, metricsApi),
  session: new SessionApi(messenger, configManager, gettify, metricsApi),
  settings: new SettingsApi(messenger, configManager, gettify, metricsApi),
  shell: new ShellApi(messenger, configManager, gettify, metricsApi),
  sidenav: new SidenavApi(messenger, configManager, gettify, metricsApi),
  user: new UserApi(messenger, configManager, gettify, metricsApi)
});

const INIT_TIMEOUT = 5000;
const MAX_ALERTS = 10;

const URL_WATCHERS = {
  HASH: watchHash,
  HISTORY: watchHistory,
  SERVER: watchLocation
};

export const updateDigitalData = (config: RuntimeConfiguration, window: Window, runtime?: RuntimeObject): void => {
  if ((window as any).digitalData) {
    (window as any).digitalData.unifiedShell = {config: sanitizeConfiguration(config)};
    digitalDataUpdated('unifiedShell', window);
    return;
  }
  runtime?.on('digitalData', () => updateDigitalData(config, window));
};

export const trackEvents = (appId: string, externalQueryParams: string, win: Window & typeof globalThis) =>
  import('@exc/events').then((events: typeof import('@exc/events')) => {
    const debug = getQueryParams(externalQueryParams)['shell_debugOmega'];
    events.trackOmega(`${appId}.omega`, win, Boolean(debug));
  });

export const listenToUserActions = (config: RuntimeConfiguration, messenger: RuntimeMessenger, win: Window & typeof globalThis, allEvents = true) => {
  const {document} = win;
  const allowedHosts = [win.location.host];
  const {onClick, onContextMenu, onKeyDown, onMouseDown, reportActivity} = createActions(messenger, win);
  win.addEventListener('mousemove', reportActivity);
  win.addEventListener('scroll', reportActivity);

  if (allEvents) {
    // Register click and contextmenu on document as click propagation does not make it
    // to window in all cases with react spectrum v3.
    document.addEventListener('contextmenu', e => onContextMenu(e, config, allowedHosts));

    // Note: onClick, onKeyDown and onMouseDown already call reportActivity.
    document.addEventListener('click', e => onClick(e, config, allowedHosts));
    win.addEventListener('keydown', e => onKeyDown(e));
    win.addEventListener('mousedown', e => onMouseDown(e, config, allowedHosts));
  } else {
    // In nested applications, we only want to track user activity for now.
    // This means keyboard shortcuts for debugger, search etc. currently won't work in nested iframes.
    document.addEventListener('click', reportActivity);
    win.addEventListener('keydown', reportActivity);
    win.addEventListener('mousedown', reportActivity);
  }
};

export const configureGainsight = (gainsight: Gainsight, win: Window & typeof globalThis, gettify: GettifyFn) => {
  const {adobeMetrics} = win;
  const w = win as any;

  // Update the opt-out status of metrics SDK based on user consent permission.
  const metricsOptIn = gainsight?.user?.permissions?.adobeUsageDataCollection;
  typeof metricsOptIn === 'boolean' && (adobeMetrics as any).optOut(!metricsOptIn);

  w.shellGainsight = {...gainsight, user: {...gainsight?.user}};
  w.shellGainsightEnabled = gainsight?.enabled || false;
  // Handle user permissions differently since we need to ensure it's cloned
  // and does not get triggered by the metrics calls.
  if (gainsight?.user?.permissions) {
    w.shellGainsight.user.permissions = {...w.shellGainsight.user.permissions};
    gettify(w.shellGainsight.user.permissions);
  }
};

/**
 * Checks for the window.digitalData object required for Target
 * Recommendations service and stores the value to check for changes.
 */
const onPageChange = function(runtime: RuntimeObject, window: Window): void {
  const win = window as any;
  let digitalDataTimeout: any;
  //, onDigitalData: (dd: any) => void
  const check = (attempt: number): void => {
    if (attempt > 5) {
      return;
    }

    const dd = win.digitalData;
    if (!dd) {
      digitalDataTimeout = window.setTimeout(() => check(attempt + 1), 1000);
      return;
    }

    runtime.digitalData = JSON.stringify(dd);
    runtime.emit('digitalData');
  };

  window.clearTimeout(digitalDataTimeout);
  check(1);
};

/**
 * Add a style element to the document head of the iframe that styles the
 * body and appContainer elements so that the side nav fits perfectly in the
 * page.
 * @param config - Runtime configuration object.
 * @param document - window document.
 */
const addSideNavStyles = (config: RuntimeConfiguration, document: Document): void => {
  const {appContainer, shellHeight, shellSideNavPresent, shellSideNavWidth} = config;
  const defaultStyles = `
    body ${appContainer} {
      overflow: auto;
      height: calc(100vh - ${shellHeight}px);
      transition: all 0.25s cubic-bezier(0.4, 0.0, 0.2, 1);
    }
  `;

  let sideStyl = document.getElementById('shell-side-style');
  const inDOM = !!sideStyl;
  if (!sideStyl) {
    sideStyl = document.createElement('style');
    sideStyl.id = 'shell-side-style';
  }
  sideStyl.innerHTML = defaultStyles;

  // Ensure the styles are in the DOM.
  inDOM || document.head.append(sideStyl);

  if (!shellSideNavPresent || !shellSideNavWidth) {
    // When user changes side nav visibility with hamburger, style should reset
    sideStyl.innerHTML = defaultStyles;
    return;
  }
  sideStyl.innerHTML = `
    body {
      overflow: hidden;
      margin-left: 0;
    }

    body ${appContainer} {
      margin-left: ${shellSideNavWidth}px;
      overflow: auto;
      height: calc(100vh - ${shellHeight}px);
      transition: all 0.25s cubic-bezier(0.4, 0.0, 0.2, 1);
    }
  `;
};

/**
 * Add style element to the DOM that sets up the shell height necessary for
 * fullscreen takeover functionality.
 * @param shellHeight - Height of the Shell UI.
 * @param document - Window Document.
 */
const addPlaceholderStyle = ({shellHeight}: {shellHeight: number}, document: Document): void => {
  // If shellHeight is not set, don't add this yet. It will be set later.
  if (!shellHeight) {
    return;
  }
  let styl = document.getElementById('shell-placeholder-style');
  const inDOM = !!styl;
  if (!styl) {
    styl = document.createElement('style');
    styl.id = 'shell-placeholder-style';
  }
  styl.innerHTML = `#shell-placeholder {height: ${shellHeight}px;}`;
  inDOM || document.head.append(styl);
};

export const done = (timestamp: number, messenger: RuntimeMessenger, metricsApi: MetricsApi, window: Window): Promise<PerformanceRecord> => {
  const {document, performance} = window;
  const version = getSPAVersion(window);
  const metrics = metricsApi.create('exc.module-runtime');
  const iframePerformance = JSON.stringify(getPerformanceEntries(timestamp, performance));
  const mainElementCount = document.querySelectorAll('main, [role="main"]').length;
  if (mainElementCount !== 1) {
    // eslint-disable-next-line no-console
    console.warn(`Detected ${mainElementCount} main elements on the page. Please ensure there is exactly one main element.`);
  }
  metrics.event(['exc.metrics.pageState.load.done', 'Runtime.done']);

  window.requestAnimationFrame(() => {
    const hasRSP = !!document.querySelector('.react-spectrum-provider');
    metrics.event('rsp.detected', hasRSP);
    metrics.event(
      'Runtime.ExcAppVersion',
      {version: (window['exc-module-runtime'] as any)?.['exc-app-version'] || 'legacy'}
    );
  });

  return sendAndReceive<DonePayload, PerformanceRecord>(Events.DONE, messenger, {addMainElement: mainElementCount === 0, iframePerformance, version});
};

const waitForInit = (
  runtimeInstance: {instance: Runtime|undefined},
  metrics: Metrics,
  messenger: RuntimeMessenger,
  win: Window): void => {
  let counter = 0;
  let sandboxInformed = false;
  let excReadyCalled = false;
  const instanceCheck = win.setInterval(() => {
    if (!runtimeInstance.instance) {
      sandboxInformed || messenger.send(Events.NOT_INITIALIZED);
      const excReady = !!win.EXC_MR_READY;
      let autoDelete = false;
      if (win.EXC_MR_READY?.autoDelete && !excReadyCalled) {
        autoDelete = true;
        win.EXC_MR_READY();
        excReadyCalled = true;
      }
      if (counter === MAX_ALERTS) {
        win.clearInterval(instanceCheck);
        metrics.warn(
          `Runtime not instantiated after ${INIT_TIMEOUT * MAX_ALERTS}ms.`,
          {autoDelete, excReady, excReadyCalled}
        );
        return;
      }
      metrics.warn(
        'Runtime not instantiated yet.',
        {autoDelete, excReady, excReadyCalled}
      );
      counter++;
      sandboxInformed = true;
      return;
    }
    win.clearInterval(instanceCheck);
  }, INIT_TIMEOUT);
};

/**
 * This function prepares the events to be sent to the iframe.
 * @param e The event from the outer shell.
 * @param metrics Metrics
 * @param document
 */
const onShellUserInput = function (e: UserKeyEvent | UserMouseEvent, metrics: Metrics, document: Document): void {
  metrics.event('Runtime.onShellUserInput', e);

  let triggerEvent: CustomKeyboardEvent | CustomMouseEvent | null = null;
  switch (e.type) {
    case 'keyup':
    case 'keydown':
      triggerEvent = new KeyboardEvent(e.type, {...e, bubbles: true});
      break;
    case 'click':
    case 'mouseup': {
      triggerEvent = new MouseEvent(e.type, {...e, bubbles: true});
      break;
    }
  }
  if (triggerEvent) {
    triggerEvent.data = e.data;
    triggerEvent.fromShell = true;
    document.body.dispatchEvent(triggerEvent);
  }
};

export default (
  messenger: RuntimeMessenger,
  configManager: ConfigProxyManager,
  listeners: ConfigureListener[],
  metricsApi: MetricsApi,
  window: Window & typeof globalThis
): RuntimeEngineSync => {
  const {adobeMetrics, document} = window;
  const gettify = createGettify(metricsApi, window);
  const modules = createModules(messenger, configManager, gettify, metricsApi);
  const apiInstances = listeners.concat(Object.values(modules));
  const metrics = metricsApi.create('exc.module-runtime');
  const recents = createRecents(messenger, window);
  let receivedEmptyToken = false;
  let receivedInvalidToken = false;

  (adobeMetrics as AdobeMetricsWithRecents).onRecents = (event: string | string[], args: any[]) => recents.track(event, args);

  /**
   * Validate a token and send an EIM warning if invalid.
   */
  const ensureValidToken = ({baseUrl, imsClientId, imsProfile, imsToken}: RuntimeConfiguration): void => {
    if (!imsToken) {
      metrics.warn('Valid Check: Empty token received');
      receivedEmptyToken = true;
    }

    if (receivedEmptyToken) {
      metrics.info('Valid Check: Valid token received after missing');
      receivedEmptyToken = false;
    }

    const {client_id, user_id} = extractTokenMetadata(imsToken) || {};
    const isValidClientId = client_id === imsClientId;
    const isValidUserId = user_id === imsProfile?.userId;

    if (isValidClientId && isValidUserId) {
      receivedInvalidToken && metrics.info('Valid Check: Valid token received after invalid');
      receivedInvalidToken = false;
      return;
    }

    receivedInvalidToken = true;
    metrics.warn('Valid Check: Invalid token received', {
      baseUrl,
      configClientId: imsClientId,
      emptyToken: !imsToken,
      extractedClientId: client_id,
      isValidClientId,
      isValidUserId
    });
  };

  const onInternalHistoryChange = (
    runtime: RuntimeObject,
    state: Record<string, any>,
    title: string,
    path?: string,
    config?: RuntimeConfiguration
  ): void => {
    metrics.event('Runtime.onLocationChange', {path, state, title});
    const href = window.location.href;

    // Check if this event is a valid recent event. If yes, trigger the metrics
    // recent event.
    if (recents.isValid(href, config?.appId)) {
      const recentEventConfig = recents.createConfig();
      metrics.recent('recents.onLocationChange', {...recentEventConfig, title});
    }
    messenger.send(Events.HISTORY, {href, path, state, title});
    runtime.emit('history', {path, type: 'internal'});
    onPageChange(runtime, window);
  };

  const onConfigure = async (runtime: RuntimeObject, config: RuntimeConfiguration, internal: InternalApi): Promise<void> => {
    apiInstances.forEach(module => module.onConfigure(config));

    const {
      analytics,
      appContainer,
      appId,
      externalQueryParams,
      gainsight,
      historyType,
      inModal,
      toastQuietModeEnabled
    } = config;

    // appContainer specific functionality that modifies the DOM and adds styles
    // in order to support the Shell header and side nav, in addition to modals.
    if (appContainer && !inModal) {
      addShellPlaceholder(document, messenger);
      addPlaceholderStyle(config, document);
      addSideNavStyles(config, document);
    }

    ensureValidToken(config);
    recents.receiveConfig(config);
    const metricsConfig = addVersionToMetricsConfig(config.metricsConfig as Configuration, window);
    await internal.configureMetrics(metricsConfig);

    configureGainsight(gainsight, window, gettify);

    // Sets the last received configuration event on the runtime object.
    runtime.lastConfigurationPayload = config;

    // Configure and update network api with latest config.
    internal.configureNetwork(config);
    runtime.sendConfig(config);
    updateDigitalData(config, window, runtime);

    // Toast usage tracking.
    enableToastTracking(messenger, metricsApi, window, toastQuietModeEnabled);

    // If debug mode is enabled via query param on the Unified Shell, console
    // out all configuration events received.
    if (getQueryParams(externalQueryParams)['shell_debug'] === 'true') {
      console.log('CONFIG:', config); // eslint-disable-line no-console
    }

    // Only continue on the initial receipt of the `configure` event
    if (runtime.configured) {
      return;
    }

    configureResolve();

    // On the ready event add omega tracking to the page
    trackEvents(appId, externalQueryParams, window);

    const version = getSPAVersion(window);
    analytics && internal.configureMetrics({analytics: {...analytics, version}});

    listenToUserActions(config, messenger, window);

    runtime.configured = true;
    URL_WATCHERS[historyType](
      window,
      (data: Record<string, any>, title: string, url?: string) => onInternalHistoryChange(runtime, data, title, url, config),
      (title: string) => messenger.send(Events.TITLE, {title})
    );
  };

  return {
    configurePromise,
    modules,
    onConfigure,
    onPageChange: runtime => onPageChange(runtime, window),
    onShellUserInput: (e: UserKeyEvent | UserMouseEvent) => onShellUserInput(e, metrics, document),
    waitForInit: (runtimeInstance: {instance: Runtime|undefined}) => waitForInit(runtimeInstance, metrics, messenger, window)
  };
};
